import { memo, lazy, Suspense } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ErrorBoundary from '../ErrorBoundary'
import { EASE_OUT, EASE_IN, SPRING } from '../../theme/motion'

const ChatMarkdown = lazy(() => import('./ChatMarkdown'))

export const SUGGESTIONS = [
  { label: 'Wie viel Solar gerade?', icon: 'wb_sunny' },
  { label: 'Ist die Haustür zu?', icon: 'door_front' },
  { label: 'Wie warm ist es morgen?', icon: 'thermostat' },
  { label: 'Rollläden Wohnzimmer zu', icon: 'roller_shades' },
  { label: 'Küche Licht an', icon: 'lightbulb' },
  { label: 'Was steht heute an?', icon: 'event' },
]

const POP = { type: 'spring', stiffness: 420, damping: 32, mass: 0.7 }

/** "claude:claude-sonnet-5-5" → "Sonnet 5.5", "ollama:gemma4:e4b" → "gemma4:e4b" */
export function modelLabel(model) {
  if (!model) return null
  const id = String(model).replace(/^(ollama|gemini|claude):/, '')
  if (id.startsWith('claude-')) {
    const s = id.slice(7).replace(/-(\d+)-(\d+)$/, ' $1.$2').replace(/-(\d+)$/, ' $1')
    return s.charAt(0).toUpperCase() + s.slice(1)
  }
  return id.replace(/^gemini-/, 'Gemini ')
}

// Chips for what the turn actually changed — the model's words can't fake these.
const AgentActions = memo(({ actions }) => {
  if (!actions?.length) return null
  return (
    <div className="mt-2 flex flex-col gap-1">
      {actions.map((a, i) => {
        const icon = a.declined ? 'block' : a.ok ? 'check_circle' : 'error'
        const tone = a.declined ? 'text-outline' : a.ok ? 'text-primary' : 'text-error'
        return (
          <motion.div
            key={i}
            className="chat-action"
            title={a.error ?? ''}
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.25, ease: EASE_OUT, delay: i * 0.06 }}
          >
            <motion.span
              className={`material-symbols-outlined flex-shrink-0 ${tone}`}
              style={{ fontSize: '1em' }}
              initial={{ scale: 0.4, rotate: -30 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ ...POP, delay: 0.08 + i * 0.06 }}
            >{icon}</motion.span>
            <span className={a.declined ? 'text-outline line-through' : 'text-on-surface-variant'}>{a.summary}</span>
            {!a.ok && !a.declined && <span className="text-error">· fehlgeschlagen</span>}
          </motion.div>
        )
      })}
    </div>
  )
})
AgentActions.displayName = 'AgentActions'

// Risky tools (door, appliances, plugs) wait for this yes/no before anything runs.
const ConfirmCard = memo(({ confirm, onAnswer, busy, voiceHint }) => {
  if (!confirm) return null
  const answered = confirm.answer != null
  return (
    <motion.div
      className="chat-confirm"
      initial={{ opacity: 0, scale: 0.92, y: 6 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={POP}
    >
      <div className="flex items-start gap-2">
        <motion.span
          className="material-symbols-outlined flex-shrink-0 chat-confirm-icon"
          animate={answered ? { scale: 1 } : { scale: [1, 1.18, 1] }}
          transition={answered ? { duration: 0.2 } : { duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
        >gpp_maybe</motion.span>
        <div className="flex flex-col min-w-0">
          <span className="font-label uppercase tracking-wider text-outline chat-confirm-eyebrow">Bestätigen</span>
          <span className="font-headline font-semibold text-on-surface">{confirm.summary}</span>
        </div>
      </div>
      <AnimatePresence mode="wait" initial={false}>
        {answered ? (
          <motion.span
            key="answered"
            className="flex items-center gap-1 text-outline chat-confirm-done"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: EASE_OUT }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '1em' }}>{confirm.answer ? 'check' : 'close'}</span>
            {confirm.answer ? 'Bestätigt' : 'Abgelehnt'}
          </motion.span>
        ) : (
          <motion.div key="ask" className="flex flex-col gap-1.5" exit={{ opacity: 0, y: -4, transition: { duration: 0.12, ease: EASE_IN } }}>
            <div className="flex gap-1.5">
              <motion.button whileTap={{ scale: 0.94 }} type="button" className="btn btn-primary chat-confirm-btn" disabled={busy} onClick={() => onAnswer(confirm.id, true)}>
                <span className="material-symbols-outlined" style={{ fontSize: '1.1em' }}>check</span>Ausführen
              </motion.button>
              <motion.button whileTap={{ scale: 0.94 }} type="button" className="btn btn-secondary chat-confirm-btn" disabled={busy} onClick={() => onAnswer(confirm.id, false)}>
                <span className="material-symbols-outlined" style={{ fontSize: '1.1em' }}>close</span>Abbrechen
              </motion.button>
            </div>
            {voiceHint && <span className="text-outline-faint chat-confirm-eyebrow">oder sag „ja“ / „nein“</span>}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
})
ConfirmCard.displayName = 'ConfirmCard'

function TypingDots({ status }) {
  return (
    <div className="flex items-center gap-2 py-0.5">
      <div className="flex items-center gap-1 text-primary">
        {[0, 1, 2].map(i => (
          <motion.span
            key={i}
            className="chat-dot"
            animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15, ease: 'easeInOut' }}
          />
        ))}
      </div>
      <AnimatePresence mode="wait" initial={false}>
        {status && (
          <motion.span
            key={status}
            className="text-outline chat-status"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18, ease: EASE_OUT }}
          >{status}</motion.span>
        )}
      </AnimatePresence>
    </div>
  )
}

// ── One message ──────────────────────────────────────────────────────────────
export const MessageBubble = memo(({ m, onConfirm, busy, voiceHint }) => {
  const isUser = m.role === 'user'
  const meta = m.meta
  return (
    <motion.div
      layout="position"
      className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}
      initial={{ opacity: 0, y: 14, x: isUser ? 10 : -10, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, x: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96, transition: { duration: 0.14, ease: EASE_IN } }}
      transition={{ ...POP, layout: SPRING }}
      style={{ originX: isUser ? 1 : 0 }}
    >
      <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} w-full gap-2`}>
        {!isUser && (
          <motion.span
            className="material-symbols-outlined flex-shrink-0 text-primary chat-avatar"
            animate={m.streaming ? { rotate: [0, 18, -12, 0], scale: [1, 1.15, 1] } : { rotate: 0, scale: 1 }}
            transition={m.streaming ? { duration: 1.8, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.3 }}
          >auto_awesome</motion.span>
        )}
        <div className={`chat-bubble ${isUser ? 'chat-bubble-user' : 'chat-bubble-ai'} ${m.error ? 'chat-bubble-error' : ''} ${!isUser && !m.text && m.confirm && !m.actions?.length ? 'chat-bubble-bare' : ''}`}>
          {isUser ? (
            <>
              {m.voice && <span className="material-symbols-outlined chat-voice-mark" title="Gesprochen">mic</span>}
              {m.text}
            </>
          ) : (
            <>
              {m.text ? (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
                  <ErrorBoundary fallback={() => <span style={{ whiteSpace: 'pre-wrap' }}>{m.text}</span>}>
                    <Suspense fallback={<span style={{ whiteSpace: 'pre-wrap' }}>{m.text}</span>}>
                      <ChatMarkdown text={m.text} />
                    </Suspense>
                  </ErrorBoundary>
                </motion.div>
              ) : m.streaming ? <TypingDots status={m.status} /> : null}
              <AgentActions actions={m.actions} />
              <ConfirmCard confirm={m.confirm} onAnswer={onConfirm} busy={busy} voiceHint={voiceHint} />
            </>
          )}
        </div>
      </div>
      {!isUser && meta && !m.streaming && (
        <motion.span
          className="chat-meta"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.3 }}
        >
          {meta.ms >= 1000 ? `${(meta.ms / 1000).toFixed(1)} s` : `${meta.ms} ms`}
          {meta.model ? ` · ${modelLabel(meta.model)}` : ''}
          {meta.costUsd ? ` · $${meta.costUsd.toFixed(4)}` : ''}
        </motion.span>
      )}
    </motion.div>
  )
})
MessageBubble.displayName = 'MessageBubble'

// ── Empty state: greeting + suggestions ──────────────────────────────────────
/** One suggestion chip: context ones (server/services/ai/suggestions.js) carry a tone and a hint. */
export function SuggestionChip({ s, phone, i = 0, onPick, compact = false }) {
  const tone = s.tone === 'alert' ? 'is-alert' : s.tone === 'action' ? 'is-action' : ''
  return (
    <motion.button
      type="button"
      onClick={() => onPick(s.prompt ?? s.label)}
      className={`chat-suggestion ${phone && !compact ? 'chat-suggestion-lg' : ''} ${compact ? 'chat-suggestion-compact' : ''} ${tone}`}
      initial={{ opacity: 0, y: 12, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.12 } }}
      transition={{ ...POP, delay: 0.12 + i * 0.05 }}
      whileTap={{ scale: 0.95 }}
      whileHover={{ y: -1 }}
      layout="position"
    >
      <span className="material-symbols-outlined chat-suggestion-icon" style={{ fontSize: phone && !compact ? '1.15rem' : '0.95rem' }}>{s.icon}</span>
      <span className="flex flex-col min-w-0 text-left">
        <span className={compact ? 'truncate' : ''}>{s.label}</span>
        {s.hint && !compact && <span className="chat-suggestion-hint">{s.hint}</span>}
      </span>
    </motion.button>
  )
}

export function EmptyState({ name, phone, voice, onPick, context = null }) {
  // Context first (what fits the house now), topped up with the general ones.
  const ctx = context ?? []
  const fill = SUGGESTIONS.filter(s => !ctx.some(c => c.label === s.label)).slice(0, Math.max(0, 6 - ctx.length))
  const list = [...ctx, ...fill]
  const hour = new Date().getHours()
  const hello = hour < 5 ? 'Gute Nacht' : hour < 11 ? 'Guten Morgen' : hour < 18 ? 'Hallo' : 'Guten Abend'
  return (
    <div className={`flex flex-col ${phone ? 'justify-end min-h-full pb-2' : 'pt-2'} gap-5`}>
      <div className="flex flex-col gap-2 px-1">
        <motion.span
          className="material-symbols-outlined text-primary chat-hero-icon"
          initial={{ opacity: 0, scale: 0.4, rotate: -40 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 18 }}
        >auto_awesome</motion.span>
        <motion.h2
          className="font-headline font-bold text-on-surface chat-hero-title"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: EASE_OUT, delay: 0.06 }}
        >{hello}{name ? `, ${name}` : ''}.</motion.h2>
        <motion.p
          className="text-outline chat-hero-sub"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: EASE_OUT, delay: 0.12 }}
        >
          Frag nach dem Haus oder sag, was ich tun soll.{voice && phone ? ' Tippe aufs Mikrofon und sprich einfach.' : ''}
        </motion.p>
      </div>
      <div className={phone ? 'grid grid-cols-2 gap-2' : 'flex flex-wrap gap-1.5'}>
        <AnimatePresence initial={false} mode="popLayout">
          {list.map((s, i) => <SuggestionChip key={s.id ?? s.label} s={s} phone={phone} i={i} onPick={onPick} />)}
        </AnimatePresence>
      </div>
    </div>
  )
}
